// Semantic cards for project rooms / appliances (TZ.md §10).
// Backend contract is schema-free (list[dict]); these render the KNOWN fields
// from the real GET /projects/{id} response, gracefully skipping any that are absent:
//   room:      id, name, type, lighting_power, socket_count, socket_power
//   appliance: id, name, type, power (watts)
// Unknown/extra keys are ignored here; a full JSON dump remains available via ?debug=1
// in ProjectPage (kept as the existing ItemCard).
//
// i18n: labels are passed in by the parent via the `labels` prop so this stays
// translation-agnostic. ProjectPage supplies t()-resolved strings.

import { Badge } from '@/components/ui/Badge';

// Loose shapes — every field optional, matching the schema-free contract.
export interface RoomLike {
  id?: string;
  name?: string;
  type?: string;
  lighting_power?: number;
  socket_count?: number;
  socket_power?: number;
}

export interface ApplianceLike {
  id?: string;
  name?: string;
  type?: string;
  power?: number;
}

export interface RoomCardLabels {
  lighting: string;     // e.g. "освещение"
  sockets: string;      // e.g. "розеток"
  socketPower: string;  // e.g. "мощность розеток"
  watt: string;         // unit, e.g. "Вт"
  fallbackName: string; // e.g. "Помещение" (used as "{fallbackName} #N")
}

export interface ApplianceCardLabels {
  watt: string;         // unit, e.g. "Вт"
  fallbackName: string; // e.g. "Прибор"
}

function num(v: number | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export function RoomCard({
  room,
  index,
  labels,
}: {
  room: RoomLike;
  index: number;
  labels: RoomCardLabels;
}) {
  return (
    <div className="border border-border rounded-md p-3 bg-surface">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="font-medium text-ink">
          {room.name ?? `${labels.fallbackName} #${index + 1}`}
        </span>
        {room.type && <Badge tone="neutral">{room.type}</Badge>}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-3">
        {num(room.lighting_power) && (
          <span>
            {labels.lighting}: <span className="font-mono">{room.lighting_power}</span> {labels.watt}
          </span>
        )}
        {num(room.socket_count) && (
          <span>
            {labels.sockets}: <span className="font-mono">{room.socket_count}</span>
          </span>
        )}
        {num(room.socket_power) && (
          <span>
            {labels.socketPower}: <span className="font-mono">{room.socket_power}</span> {labels.watt}
          </span>
        )}
      </div>
    </div>
  );
}

export function ApplianceCard({
  appliance,
  index,
  labels,
}: {
  appliance: ApplianceLike;
  index: number;
  labels: ApplianceCardLabels;
}) {
  return (
    <div className="border border-border rounded-md p-3 bg-surface">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="font-medium text-ink">
          {appliance.name ?? `${labels.fallbackName} #${index + 1}`}
        </span>
        {appliance.type && <Badge tone="neutral">{appliance.type}</Badge>}
      </div>
      {num(appliance.power) && (
        <div className="text-xs text-ink-3">
          <span className="font-mono">{appliance.power}</span> {labels.watt}
        </div>
      )}
    </div>
  );
}
